import { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

const stats = [
  { value: '10+', label: '2026年新規導入社数', note: 'ベンチャー・中小企業を中心に' },
  { value: '5%', label: '平均スカウト返信率', note: '業界平均比 +3ポイント' },
  { value: '15h', label: '週あたり削減工数', note: '採用担当1名あたりの平均値' },
  { value: '3.2倍', label: '母集団形成の改善率', note: '導入前後6ヶ月の比較' },
];

const testimonials = [
  {
    company: 'テクノロジー系スタートアップ',
    industry: 'SaaS・IT',
    size: '従業員 30名',
    name: '採用担当 Mさん',
    comment:
      '媒体は契約していたものの、送信作業に追われるだけで改善ができていませんでした。代行をお願いしてから返信率が大幅に向上し、今では週に5〜8件の一次面談を安定的に確保できています。データを見ながら一緒に改善してもらえる点が特に助かっています。',
    result: '返信率 1% → 5%',
  },
  {
    company: '製造業 中小企業',
    industry: '製造・ものづくり',
    size: '従業員 80名',
    name: '人事責任者 Kさん',
    comment:
      '採用担当が私一人で、スカウト送信に時間を割けない状況でした。代行後は週次レポートで進捗が見えるようになり、私は面談・選考に集中できるようになりました。ターゲット設計から一緒に考えてもらえるので、初めての逆求人でも迷わずに進められました。',
    result: '工数削減 週12時間',
  },
  {
    company: 'コンサルティングファーム',
    industry: 'コンサルティング',
    size: '従業員 45名',
    name: '代表取締役 Tさん',
    comment:
      '「うちには採用担当がいない」という状態からのスタートでしたが、ターゲット定義から文面作成まで全部サポートしていただき、3ヶ月で内定承諾者が出ました。細かい改善提案を毎週もらえるので、採用活動の解像度が上がった実感があります。',
    result: '3ヶ月で内定承諾者獲得',
  },
];

export default function ResultsSection() {
  const [active, setActive] = useState(0);
  const { ref, inView } = useInView();

  return (
    <section id="results" className="py-10 md:py-20 bg-white">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-8 md:mb-14 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs tracking-widest text-[#E8601C] font-medium uppercase mb-3 block">
            RESULTS
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a2e4a] leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            導入企業の成果
          </h2>
          <p className="text-[#666] text-sm mt-4 leading-relaxed">
            数字で見る、スカウト代行の実績
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-8 md:mb-16">
          {stats.map((s, i) => (
            <div
              key={i}
              className={`text-center p-4 sm:p-6 rounded-2xl bg-[#f7f5f2] transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: inView ? `${i * 100 + 200}ms` : '0ms' }}
            >
              <p
                className="text-3xl sm:text-4xl font-bold text-[#1a2e4a] mb-1"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {s.value}
              </p>
              <p className="text-xs sm:text-sm font-medium text-[#333] mb-1">{s.label}</p>
              <p className="text-xs text-[#999]">{s.note}</p>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8 items-start transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: inView ? '600ms' : '0ms' }}>
          {/* Selector */}
          <div className="lg:col-span-2 flex flex-col gap-3">
            {testimonials.map((t, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`text-left p-4 rounded-xl transition-all duration-200 cursor-pointer ${
                  active === i
                    ? 'bg-[#1a2e4a] text-white'
                    : 'bg-[#f7f5f2] text-[#444] hover:bg-[#eee]'
                }`}
              >
                <p className={`text-xs mb-1 ${active === i ? 'text-white/60' : 'text-[#999]'}`}>
                  {t.industry} / {t.size}
                </p>
                <p className={`text-sm font-medium ${active === i ? 'text-white' : 'text-[#1a2e4a]'}`}>
                  {t.company}
                </p>
                <div className={`mt-2 inline-block text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                  active === i ? 'bg-[#E8601C] text-white' : 'bg-white text-[#E8601C]'
                }`}>
                  {t.result}
                </div>
              </button>
            ))}
          </div>

          {/* Comment */}
          <div className="lg:col-span-3 bg-[#f7f5f2] rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              {[1, 2, 3, 4, 5].map((s) => (
                <i key={s} className="ri-star-fill text-[#E8601C] text-sm" />
              ))}
              <span className="text-xs text-[#999] ml-1">満足度 5.0</span>
            </div>
            <blockquote
              className="text-[#333] text-sm sm:text-base leading-loose mb-5 sm:mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              「{testimonials[active].comment}」
            </blockquote>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-[#1a2e4a] rounded-full flex-shrink-0">
                <i className="ri-user-line text-white text-sm" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#1a2e4a]">{testimonials[active].name}</p>
                <p className="text-xs text-[#999]">{testimonials[active].company}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
